import { QUEST_STEP_DELIVER_TO_NPC } from '../config/constants'

const questAwardText = (award, prefix = '') =>
  [
    award?.xp   ? `${prefix}${award.xp} XP`   : null,
    award?.gold ? `${prefix}${award.gold} gold` : null,
  ].filter(Boolean).join(prefix ? '  ' : ', ')

// Returns { lines, questAction, startQuestId } when a quest override applies, null otherwise.
// questAction: { type: 'deliver', questId, stepId, itemId } — executed after dialog closes.
// startQuestId: questId to call startQuest() on before opening dialog.
export const buildQuestDialog = ({ quests, questProgress, inventory, heldItemId, npcId, dungeonSlug }) => {
  // 1. Held-item delivery — highest priority
  if (heldItemId) {
    const heldItem = inventory?.find(i => i.id === heldItemId)
    if (heldItem) {
      for (const quest of quests) {
        const progress = questProgress[quest.id]
        if (!progress || progress.status !== 'active') continue
        for (const step of quest.steps) {
          if (step.type !== QUEST_STEP_DELIVER_TO_NPC) continue
          if (step.targetNpcPlacementId !== npcId) continue
          if (step.dungeon_slug && dungeonSlug && step.dungeon_slug !== dungeonSlug) continue
          if (progress.completedSteps.includes(step.id)) continue
          if (heldItem.type !== step.itemType) continue
          return {
            lines: [step.description],
            questAction: { type: 'deliver', questId: quest.id, stepId: step.id, itemId: heldItem.id },
            startQuestId: null,
          }
        }
      }
    }
  }

  // 2. Quest giver dialog (intro / in-progress / complete)
  const giverQuest = quests.find(q => q.giverNpcPlacementId === npcId)
  if (giverQuest) {
    const progress = questProgress[giverQuest.id]
    const allDone = (progress?.completedSteps?.length ?? 0) >= giverQuest.steps.length

    if (!progress) {
      const awardText = questAwardText(giverQuest.award)
      const lines = giverQuest.dialog?.intro?.length
        ? giverQuest.dialog.intro
        : [
            `[Quest: ${giverQuest.name}]`,
            giverQuest.description,
            ...giverQuest.steps.map((s, i) => `${i + 1}. ${s.description}`),
            ...(awardText ? [`Reward: ${awardText}`] : []),
          ]
      return { lines, questAction: null, startQuestId: giverQuest.id }
    }

    if (progress.status === 'active' && allDone) {
      const awardText = questAwardText(giverQuest.award, '+')
      const lines = giverQuest.dialog?.complete?.length
        ? giverQuest.dialog.complete
        : [`${giverQuest.name} complete!`, ...(awardText ? [awardText] : [])]
      return { lines, questAction: null, startQuestId: null }
    }

    const lines = giverQuest.dialog?.in_progress?.length
      ? giverQuest.dialog.in_progress
      : (() => {
          const pending = giverQuest.steps.filter(s => !progress.completedSteps.includes(s.id))
          return [`[${giverQuest.name}: in progress]`, pending[0]?.description || 'Keep it up!']
        })()
    return { lines, questAction: null, startQuestId: null }
  }

  // 3. Auto-deliver fallback (no held item, match by inventory type)
  for (const quest of quests) {
    const progress = questProgress[quest.id]
    if (!progress || progress.status !== 'active') continue
    for (const step of quest.steps) {
      if (step.type !== QUEST_STEP_DELIVER_TO_NPC) continue
      if (step.targetNpcPlacementId !== npcId) continue
      if (step.dungeon_slug && dungeonSlug && step.dungeon_slug !== dungeonSlug) continue
      if (progress.completedSteps.includes(step.id)) continue
      const item = inventory?.find(i => i.type === step.itemType)
      if (!item) continue
      return {
        lines: [step.description],
        questAction: { type: 'deliver', questId: quest.id, stepId: step.id, itemId: item.id },
        startQuestId: null,
      }
    }
  }

  return null
}
